import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_BASE } from '../utils/config';
import { apiFetch } from '../utils/apiFetch';
import { useCart } from '../context/CartContext';
import { clearLensSelections } from '../utils/lensStore';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshCart } = useCart();

  const [apiError, setApiError] = useState<string | null>(null);
  const [apiSuccess, setApiSuccess] = useState(false);
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;

    // Read session_id from URL first, then localStorage fallbacks (same as old UI)
    const sessionId =
      searchParams.get('session_id') ||
      localStorage.getItem('stripe_session_id') ||
      localStorage.getItem('id');

    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('authToken');

    if (!sessionId || !userId || !token) {
      // Payment was made via Stripe — show success but skip order creation API
      // (session_id will be missing if backend doesn't embed it in redirect URL)
      cleanupLocalStorage();
      refreshCart();
      return;
    }

    called.current = true;
    sendToBackend(sessionId, userId, token);
  }, [searchParams]);

  const cleanupLocalStorage = () => {
    clearLensSelections();
    localStorage.removeItem('stripeCheckoutCartItems');
    localStorage.removeItem('lensSelectionData');
    localStorage.removeItem('stripe_session_id');
  };

  const sendToBackend = async (sessionId: string, userId: string, token: string) => {
    const storedCartItems = localStorage.getItem('stripeCheckoutCartItems');
    const lensData = localStorage.getItem('lensSelectionData');
    let items_cart: any[] = [];
    try { items_cart = storedCartItems ? JSON.parse(storedCartItems) : []; } catch {}

    try {
      const res = await fetch(`${API_BASE}/gateway_transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId, userid: userId, items_cart, lensData }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Remove each cart item from backend (matches old UI behaviour)
      await Promise.all(
        items_cart
          .filter((item: any) => item.id)
          .map((item: any) =>
            apiFetch(`${API_BASE}/cart_item/${item.id}`, { method: 'DELETE' }).catch(() => {})
          )
      );

      cleanupLocalStorage();
      await refreshCart();
      setApiSuccess(true);
    } catch (err: any) {
      setApiError('Payment recorded but order confirmation failed. Please contact support if your order is not in My Orders.');
    }
  };

  return (
    <div className="success-page">
      <div className="success-card">
        <div className="success-card__icon success-card__icon--ok">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2 className="success-card__title">Payment Successful!</h2>
        <p className="success-card__sub">
          Thank you for your order. Your payment was processed successfully.
        </p>

        {apiSuccess && (
          <div style={{ fontSize: 13, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
            Order confirmed and recorded.
          </div>
        )}

        {apiError && (
          <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
            {apiError}
          </div>
        )}

        <div className="success-card__actions">
          <button className="btn btn--outline" onClick={() => navigate('/')}>Continue Shopping</button>
          <button className="btn btn--primary" onClick={() => navigate('/orders')}>Track Order</button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
